import React, { useState, useEffect } from 'react';
import { userApiClient } from '../../api/HttpClient';
import dayjs from 'dayjs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TrendingUp, TrendingDown, Wallet, Receipt, PiggyBank, ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface MonthlySummaryTabProps {
  propertyId: string;
}

interface SummaryData {
  property_id: string;
  month: string;
  total_rent_collected: number;
  total_extra_profits: number;
  total_expenses: number;
  net_balance: number;
}

const MonthlySummaryTab: React.FC<MonthlySummaryTabProps> = ({ propertyId }) => {
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format('YYYY-MM'));
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const generateMonthOptions = () => {
    const months = [];
    let date = dayjs();
    for (let i = 0; i < 13; i++) {
      months.push(date.format('YYYY-MM'));
      date = date.subtract(1, 'month');
    }
    return months;
  };

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const client = await userApiClient;
      const data = await client.get<SummaryData>(
        `/api/pg_tenant/property/${propertyId}/monthly-summary/?month=${selectedMonth}`
      );
      setSummary(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [propertyId, selectedMonth]);

  const fmt = (n: number) => Number(n).toLocaleString('en-IN');
  const isProfit = summary ? summary.net_balance >= 0 : true;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-foreground">Monthly Financial Summary</h2>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[140px] bg-background">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent className="bg-card border z-50">
            {generateMonthOptions().map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-40">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      )}

      {error && <p className="text-red-500">Error: {error}</p>}

      {!loading && !error && summary && (
        <div className="space-y-4">
          {/* Net Balance Hero Card */}
          <div className={`rounded-2xl p-6 text-white ${isProfit ? 'bg-gradient-to-br from-emerald-600 to-emerald-700' : 'bg-gradient-to-br from-red-500 to-red-600'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm font-medium mb-1">{dayjs(selectedMonth).format('MMMM YYYY')} — Net Balance</p>
                <p className="text-4xl font-bold tracking-tight">
                  {isProfit ? '+' : ''}₹{fmt(summary.net_balance)}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  {isProfit
                    ? <><TrendingUp className="h-4 w-4" /><span className="text-sm">Profitable month</span></>
                    : <><TrendingDown className="h-4 w-4" /><span className="text-sm">Loss this month</span></>
                  }
                </div>
              </div>
              <div className="bg-white/15 rounded-2xl p-4">
                <Wallet className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          {/* Breakdown Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Rent Collected */}
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-primary/10 rounded-lg p-2">
                  <Receipt className="h-5 w-5 text-primary" />
                </div>
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">Rent</span>
              </div>
              <p className="text-2xl font-bold text-foreground">₹{fmt(summary.total_rent_collected)}</p>
              <p className="text-sm text-muted-foreground mt-1">Rent collected</p>
            </div>

            {/* Extra Profits */}
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-emerald-100 dark:bg-emerald-900/30 rounded-lg p-2">
                  <ArrowUpRight className="h-5 w-5 text-emerald-600" />
                </div>
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">Extra</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">+₹{fmt(summary.total_extra_profits)}</p>
              <p className="text-sm text-muted-foreground mt-1">Extra profits</p>
            </div>

            {/* Expenses */}
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-2">
                  <ArrowDownRight className="h-5 w-5 text-red-500" />
                </div>
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">Expenses</span>
              </div>
              <p className="text-2xl font-bold text-red-500">−₹{fmt(summary.total_expenses)}</p>
              <p className="text-sm text-muted-foreground mt-1">Total expenses</p>
            </div>
          </div>

          {/* Formula Bar */}
          <div className="bg-muted/40 border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-3">Calculation</p>
            <div className="flex items-center gap-2 flex-wrap text-sm font-semibold">
              <span className="text-foreground">₹{fmt(summary.total_rent_collected)}</span>
              <span className="text-muted-foreground">Rent</span>
              <span className="text-emerald-600 font-bold">+</span>
              <span className="text-emerald-600">₹{fmt(summary.total_extra_profits)}</span>
              <span className="text-muted-foreground">Extra</span>
              <span className="text-red-500 font-bold">−</span>
              <span className="text-red-500">₹{fmt(summary.total_expenses)}</span>
              <span className="text-muted-foreground">Expenses</span>
              <span className="text-muted-foreground">=</span>
              <span className={`text-lg font-bold ${isProfit ? 'text-emerald-600' : 'text-red-500'}`}>
                {isProfit ? '+' : ''}₹{fmt(summary.net_balance)}
              </span>
            </div>
          </div>

          {/* Empty state hint */}
          {summary.total_rent_collected === 0 && summary.total_extra_profits === 0 && summary.total_expenses === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <PiggyBank className="h-10 w-10 mx-auto mb-2 opacity-30" />
              No financial data recorded for {dayjs(selectedMonth).format('MMMM YYYY')}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MonthlySummaryTab;
